/*
 * Copyright 2022, Corning Optical Communications
 * All Rights Reserved.
 */
import { useLocation, useNavigate } from "react-router-dom";
import { useEffect } from "react";
import { deviceActionSelector, noOfSelectedRowSelector } from "../../store/api/devices/selectors";
import { useSelector } from "react-redux";
import { tasksSelector } from "../../store/api/tasks/selector";
import { IAddScheduledTask } from "../../store/api/tasks/types";
import { actionPayload, rowSelectionKey, taskActionPayload } from "./ActionHelper";
import { setBasicDialogOpenFor, setIsLoadingStatus, setOpenToast } from "../../store/helpers/helperReducers";
import { useStoreDispatch } from "../../store/hooks";
import { setScheduledTask } from "../../store/api/tasks/reducer";
import { useRunOnDemandTaskMutation } from "../../store/api/tasks/taskApiSlice";
import { useDeleteDeviceMutation } from "../../store/api/devices/deviceApiSlice";
import {
  useSetDeviceConfigMutation,
  useSetRequestFullRefreshMutation,
  useSetRequestPartialRefreshMutation,
  useTriggerDasDiscoveryMutation,
} from "../../store/api/deviceConfig/deviceConfigApiSlice";
import { setNoOfSelectedRow, setOpenDeviceDrawer } from "../../store/api/devices/reducers";
import { useNodeDisplay } from "../../pages/Nodes/NodeDisplay/hooks";
import { errorMessage } from "../../utils/helpers";
import { IKeyValueArray } from "../Administrations/types";

export const useTaskAction = (tableId?: string) => {
  const deviceAction = useSelector(deviceActionSelector);
  const dispatch = useStoreDispatch();
  const [runOnDemandTask, runOnDemandTaskStatus] = useRunOnDemandTaskMutation();

  const handleRemoveSelectedRow = (tableRowSelection: IKeyValueArray) => {
    if (tableId) {
      dispatch(setNoOfSelectedRow({ ...tableRowSelection }));
    } else return;
  };

  const taskActionGeneralSubmitHandler = async () => {
    let createTaskResponse: any;
    const tableRowSelection = rowSelectionKey(tableId || "");
    try {
      const payload = taskActionPayload[deviceAction.taskName](deviceAction);
      dispatch(setBasicDialogOpenFor(undefined));
      if (payload) {
        createTaskResponse = await runOnDemandTask(payload);
      }
      if (createTaskResponse?.error) throw createTaskResponse.error;
      if (createTaskResponse?.data) {
        dispatch(setScheduledTask({}));
        dispatch(
          setOpenToast({
            openToast: true,
            toastType: "success",
            message: `The task ${deviceAction.name} has been executed sucessfully.`,
          })
        );
        handleRemoveSelectedRow(tableRowSelection);
      } else {
        dispatch(
          setOpenToast({
            openToast: true,
            toastType: "error",
            message: `The task ${deviceAction.name} execution failed.`,
          })
        );
        handleRemoveSelectedRow(tableRowSelection);
      }
    } catch (error) {
      dispatch(
        setOpenToast({
          openToast: true,
          toastType: "error",
          message: `The task ${deviceAction.name} execution failed. ` + errorMessage(error),
        })
      );
      handleRemoveSelectedRow(tableRowSelection);
    }
  };

  return {
    taskActionGeneralSubmitHandler,
    runOnDemandTaskStatus,
  };
};

export const useTaskActionComponent = () => {
  const deviceAction = useSelector(deviceActionSelector);
  let tasksFormData: IAddScheduledTask = useSelector(tasksSelector);
  const dispatch = useStoreDispatch();
  const [runOnDemandTask, runOnDemandTaskStatus] = useRunOnDemandTaskMutation();

  useEffect(() => {
    dispatch(setIsLoadingStatus({ upgrade: runOnDemandTaskStatus.isLoading }));
  }, [dispatch, runOnDemandTaskStatus.isLoading]);

  const taskActionSubmitHandler = async () => {
    let createTaskResponse: any;
    try {
      const payload = taskActionPayload[deviceAction.taskName](tasksFormData);
      if (payload) {
        createTaskResponse = await runOnDemandTask(payload);
      }
      if (createTaskResponse?.error) throw createTaskResponse.error;
      if (createTaskResponse?.data) {
        dispatch(
          setOpenToast({
            openToast: true,
            toastType: "success",
            message: `The task ${tasksFormData.name} has been executed sucessfully.`,
          })
        );
        dispatch(setBasicDialogOpenFor(undefined));
        dispatch(setScheduledTask({}));
        dispatch(setNoOfSelectedRow({ deviceRowIds: [] }));
      }
    } catch (error) {
      dispatch(
        setOpenToast({
          openToast: true,
          toastType: "error",
          message: errorMessage(error),
        })
      );
      dispatch(setNoOfSelectedRow({ deviceRowIds: [] }));
    }
  };

  return {
    taskActionSubmitHandler,
    runOnDemandTaskStatus,
  };
};

export const useGeneralAction = (tableId?: string) => {
  const navigate = useNavigate();
  const location = useLocation();
  const locationPath = location?.pathname.split("/");
  const deviceAction = useSelector(deviceActionSelector);
  const dispatch = useStoreDispatch();
  const [removeDevice] = useDeleteDeviceMutation();
  const [refreshDevice] = useSetRequestFullRefreshMutation();
  const [setDeviceConfig] = useSetDeviceConfigMutation();
  const [setPartialRefresh] = useSetRequestPartialRefreshMutation();
  const [triggerDasDiscovery] = useTriggerDasDiscoveryMutation();
  const { refetch } = useNodeDisplay();

  const handleRemoveSelectedRow = (tableRowSelection: IKeyValueArray) => {
    if (tableId) {
      dispatch(setNoOfSelectedRow({ ...tableRowSelection }));
    } else return;
  };

  const actionSubmitHandler = async () => {
    const tableRowSelection = rowSelectionKey(tableId || "");
    try {
      const payload = actionPayload[deviceAction.actionName](deviceAction);
      dispatch(setBasicDialogOpenFor(undefined));
      let result: any;
      if (payload) {
        if (deviceAction.actionName === "Delete") {
          result = await removeDevice(payload.deviceKey);
        } else if (deviceAction.actionName === "RefreshFullDevice") {
          result = await refreshDevice(payload.deviceKey);
        } else if (deviceAction.actionName === "RadioNodeEnable" || deviceAction.actionName === "RadioNodeDisable") {
          result = await setDeviceConfig(payload);
        } else if (deviceAction.actionName === "PartialRefresh") {
          result = await setPartialRefresh(payload);
        } else if (deviceAction.actionName === "DuEnable" || deviceAction.actionName === "DuDisable") {
          result = await setDeviceConfig(payload);
        } else if (deviceAction.actionName === "DasDiscoveryRefresh") {
          result = await triggerDasDiscovery(payload.deviceKey);
        }
      }

      if (result?.error) throw result?.error;
      if (result?.data?.status === 200) {
        if (deviceAction.actionName === "Delete") {
          dispatch(setOpenDeviceDrawer(false));
          navigate("/nodes");
        }
        locationPath?.length <= 2 && refetch();
        dispatch(
          setOpenToast({
            openToast: true,
            toastType: "success",
            message: `The Action ${deviceAction.name} has been executed sucessfully.`,
          })
        );
        handleRemoveSelectedRow(tableRowSelection);
      }
    } catch (error) {
      dispatch(
        setOpenToast({
          openToast: true,
          toastType: "error",
          message: `The Action ${deviceAction.name} execution failed.` + errorMessage(error),
        })
      );
      handleRemoveSelectedRow(tableRowSelection);
    }
  };

  return {
    actionSubmitHandler,
  };
};
